/**
 * Where the moon is in the sky, accurate to a few hundredths of a degree
 * (ERI-262). SunCalc's moon uses a short series with no parallax and was up to
 * 3.6 degrees off in altitude, which put moonrise and moonset up to 25 minutes
 * out. This is the Meeus (Astronomical Algorithms, ch. 47) lunar series, made
 * topocentric for the observer, with the same almanac refraction the galactic
 * core uses.
 *
 * Mirrored byte for byte in supabase/functions/_shared/moon-position.ts. No
 * imports, so the two copies stay identical (a test checks).
 */

const RAD = Math.PI / 180;
/** TT minus UTC, seconds (32.184 + 37 leap seconds since 2017). */
const TT_MINUS_UTC = 69.184;
const EARTH_RADIUS_KM = 6378.14;
const MOON_RADIUS_KM = 1737.4;

// Meeus table 47.A: D, M, M', F, longitude (1e-6 deg), distance (1e-3 km).
const LR: number[][] = [
  [0, 0, 1, 0, 6288774, -20905355], [2, 0, -1, 0, 1274027, -3699111], [2, 0, 0, 0, 658314, -2955968],
  [0, 0, 2, 0, 213618, -569925], [0, 1, 0, 0, -185116, 48888], [0, 0, 0, 2, -114332, -3149],
  [2, 0, -2, 0, 58793, 246158], [2, -1, -1, 0, 57066, -152138], [2, 0, 1, 0, 53322, -170733],
  [2, -1, 0, 0, 45758, -204586], [0, 1, -1, 0, -40923, -129620], [1, 0, 0, 0, -34720, 108743],
  [0, 1, 1, 0, -30383, 104755], [2, 0, 0, -2, 15327, 10321], [0, 0, 1, 2, -12528, 0],
  [0, 0, 1, -2, 10980, 79661], [4, 0, -1, 0, 10675, -34782], [0, 0, 3, 0, 10034, -23210],
  [4, 0, -2, 0, 8548, -21636], [2, 1, -1, 0, -7888, 24208], [2, 1, 0, 0, -6766, 30824],
  [1, 0, -1, 0, -5163, -8379], [1, 1, 0, 0, 4987, -16675], [2, -1, 1, 0, 4036, -12831],
  [2, 0, 2, 0, 3994, -10445], [4, 0, 0, 0, 3861, -11650], [2, 0, -3, 0, 3665, 14403],
  [0, 1, -2, 0, -2689, -7003], [2, 0, -1, 2, -2602, 0], [2, -1, -2, 0, 2390, 10056],
  [1, 0, 1, 0, -2348, 6322], [2, -2, 0, 0, 2236, -9884], [0, 1, 2, 0, -2120, 5751],
  [0, 2, 0, 0, -2069, 0], [2, -2, -1, 0, 2048, -4950], [2, 0, 1, -2, -1773, 4130],
  [2, 0, 0, 2, -1595, 0], [4, -1, -1, 0, 1215, -3958], [0, 0, 2, 2, -1110, 0],
  [3, 0, -1, 0, -892, 3258], [2, 1, 1, 0, -810, 2616], [4, -1, -2, 0, 759, -1897],
  [0, 2, -1, 0, -713, -2117], [2, 2, -1, 0, -700, 2354], [2, 1, -2, 0, 691, 0],
  [2, -1, 0, -2, 596, 0], [4, 0, 1, 0, 549, -1423], [0, 0, 4, 0, 537, -1117],
  [4, -1, 0, 0, 520, -1571], [1, 0, -2, 0, -487, -1739], [2, 1, 0, -2, -399, 0],
  [0, 0, 2, -2, -381, -4421], [1, 1, 1, 0, 351, 0], [3, 0, -2, 0, -340, 0],
  [4, 0, -3, 0, 330, 0], [2, -1, 2, 0, 327, 0], [0, 2, 1, 0, -323, 1165],
  [1, 1, -1, 0, 299, 0], [2, 0, 3, 0, 294, 0], [2, 0, -1, -2, 0, 8752],
];

// Meeus table 47.B: D, M, M', F, latitude (1e-6 deg).
const B: number[][] = [
  [0, 0, 0, 1, 5128122], [0, 0, 1, 1, 280602], [0, 0, 1, -1, 277693], [2, 0, 0, -1, 173237],
  [2, 0, -1, 1, 55413], [2, 0, -1, -1, 46271], [2, 0, 0, 1, 32573], [0, 0, 2, 1, 17198],
  [2, 0, 1, -1, 9266], [0, 0, 2, -1, 8822], [2, -1, 0, -1, 8216], [2, 0, -2, -1, 4324],
  [2, 0, 1, 1, 4200], [2, 1, 0, -1, -3359], [2, -1, -1, 1, 2463], [2, -1, 0, 1, 2211],
  [2, -1, -1, -1, 2065], [0, 1, -1, -1, -1870], [4, 0, -1, -1, 1828], [0, 1, 0, 1, -1794],
  [0, 0, 0, 3, -1749], [0, 1, -1, 1, -1565], [1, 0, 0, 1, -1491], [0, 1, 1, 1, -1475],
  [0, 1, 1, -1, -1410], [0, 1, 0, -1, -1344], [1, 0, 0, -1, -1335], [0, 0, 3, 1, 1107],
  [4, 0, 0, -1, 1021], [4, 0, -1, 1, 833], [0, 0, 1, -3, 777], [4, 0, -2, 1, 671],
  [2, 0, 0, -3, 607], [2, 0, 2, -1, 596], [2, -1, 1, -1, 491], [2, 0, -2, 1, -451],
  [0, 0, 3, -1, 439], [2, 0, 2, 1, 422], [2, 0, -3, -1, 421], [2, 1, -1, 1, -366],
  [2, 1, 0, 1, -351], [4, 0, 0, 1, 331], [2, -1, 1, 1, 315], [2, -2, 0, -1, 302],
  [0, 0, 1, 3, -283], [2, 1, 1, -1, -229], [1, 1, 0, -1, 223], [1, 1, 0, 1, 223],
  [0, 1, -2, -1, -220], [2, 1, -1, -1, -220], [1, 0, 1, 1, -185], [2, -1, -2, -1, 181],
  [0, 1, 2, 1, -177], [4, 0, -2, -1, 176], [4, -1, -1, -1, 166], [1, 0, 1, -1, -164],
  [4, 0, 1, -1, 132], [1, 0, -1, -1, -119], [4, -1, 0, -1, 115], [2, -2, 0, 1, 107],
];

/** Almanac refraction (Saemundsson, 10 C, 1010 hPa) added to a true altitude, degrees. */
function apparent(trueAltDeg: number): number {
  if (trueAltDeg < -1) return trueAltDeg;
  const h = trueAltDeg;
  return h + 1.02 / Math.tan((h + 10.3 / (h + 5.11)) * RAD) / 60;
}

/** Geometric topocentric position: altitude and azimuth (from south, westward) in degrees, distance in km. */
function topocentric(date: Date, latitude: number, longitude: number) {
  const jdUt = date.getTime() / 86400000 + 2440587.5;
  const T = (jdUt + TT_MINUS_UTC / 86400 - 2451545) / 36525;

  const Lp = 218.3164477 + 481267.88123421 * T - 0.0015786 * T * T + (T * T * T) / 538841;
  const D = 297.8501921 + 445267.1114034 * T - 0.0018819 * T * T + (T * T * T) / 545868;
  const M = 357.5291092 + 35999.0502909 * T - 0.0001536 * T * T;
  const Mp = 134.9633964 + 477198.8675055 * T + 0.0087414 * T * T + (T * T * T) / 69699;
  const F = 93.272095 + 483202.0175233 * T - 0.0036539 * T * T - (T * T * T) / 3526000;
  const A1 = 119.75 + 131.849 * T;
  const A2 = 53.09 + 479264.29 * T;
  const A3 = 313.45 + 481266.484 * T;
  const E = 1 - 0.002516 * T - 0.0000074 * T * T;

  let sl = 0;
  let sr = 0;
  for (const [d, m, mp, f, l, r] of LR) {
    const arg = (d * D + m * M + mp * Mp + f * F) * RAD;
    const e = m === 0 ? 1 : Math.abs(m) === 1 ? E : E * E;
    sl += l * e * Math.sin(arg);
    sr += r * e * Math.cos(arg);
  }
  let sb = 0;
  for (const [d, m, mp, f, b] of B) {
    const e = m === 0 ? 1 : Math.abs(m) === 1 ? E : E * E;
    sb += b * e * Math.sin((d * D + m * M + mp * Mp + f * F) * RAD);
  }
  sl += 3958 * Math.sin(A1 * RAD) + 1962 * Math.sin((Lp - F) * RAD) + 318 * Math.sin(A2 * RAD);
  sb += -2235 * Math.sin(Lp * RAD) + 382 * Math.sin(A3 * RAD) + 175 * Math.sin((A1 - F) * RAD)
    + 175 * Math.sin((A1 + F) * RAD) + 127 * Math.sin((Lp - Mp) * RAD) - 115 * Math.sin((Lp + Mp) * RAD);

  // Nutation (Meeus ch. 22, the short form), for the apparent position of date.
  const omega = (125.04452 - 1934.136261 * T) * RAD;
  const Ls = (280.4665 + 36000.7698 * T) * RAD;
  const dPsi = (-17.2 * Math.sin(omega) - 1.32 * Math.sin(2 * Ls) - 0.23 * Math.sin(2 * Lp * RAD) + 0.21 * Math.sin(2 * omega)) / 3600;
  const dEps = (9.2 * Math.cos(omega) + 0.57 * Math.cos(2 * Ls) + 0.1 * Math.cos(2 * Lp * RAD) - 0.09 * Math.cos(2 * omega)) / 3600;
  const eps = (23.43929111 - (46.815 * T) / 3600 + dEps) * RAD;

  const lambda = (Lp + sl / 1e6 + dPsi) * RAD;
  const beta = (sb / 1e6) * RAD;
  const dist = 385000.56 + sr / 1000;

  // Geocentric equatorial (of date), km.
  const x = dist * Math.cos(beta) * Math.cos(lambda);
  const y = dist * (Math.cos(beta) * Math.sin(lambda) * Math.cos(eps) - Math.sin(beta) * Math.sin(eps));
  const z = dist * (Math.cos(beta) * Math.sin(lambda) * Math.sin(eps) + Math.sin(beta) * Math.cos(eps));

  // Observer on the WGS84-ish ellipsoid at sea level, at local apparent sidereal time.
  const d = jdUt - 2451545;
  const gmst = 18.697374558 + 24.06570982441908 * d;
  const theta = ((((gmst + longitude / 15) % 24) + 24) % 24) * 15 * RAD + dPsi * Math.cos(eps) * RAD;
  const u = Math.atan(0.99664719 * Math.tan(latitude * RAD));
  const rhoCos = Math.cos(u) * EARTH_RADIUS_KM;
  const rhoSin = 0.99664719 * Math.sin(u) * EARTH_RADIUS_KM;
  const tx = x - rhoCos * Math.cos(theta);
  const ty = y - rhoCos * Math.sin(theta);
  const tz = z - rhoSin;

  const range = Math.sqrt(tx * tx + ty * ty + tz * tz);
  const ra = Math.atan2(ty, tx);
  const dec = Math.asin(tz / range);
  const H = theta - ra;
  const phi = latitude * RAD;
  const alt = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
  const az = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
  return { altitude: alt / RAD, azimuth: az / RAD, distance: range };
}

/**
 * The moon's centre as you see it: apparent altitude and azimuth in radians,
 * azimuth measured from south towards west, and topocentric distance in km.
 * Same shape and conventions as SunCalc.getMoonPosition, so it drops in.
 */
export function moonPosition(date: Date, latitude: number, longitude: number) {
  const p = topocentric(date, latitude, longitude);
  return { altitude: apparent(p.altitude) * RAD, azimuth: p.azimuth * RAD, distance: p.distance };
}

/**
 * Apparent altitude of the moon's top edge, in degrees. Moonrise and moonset are
 * where this crosses 0: the first or last sliver on a flat horizon.
 */
export function moonUpperLimbAltitude(date: Date, latitude: number, longitude: number): number {
  const p = topocentric(date, latitude, longitude);
  return apparent(p.altitude + Math.asin(MOON_RADIUS_KM / p.distance) / RAD);
}
